import React from 'react'

export type PhotoSize = 'small' | 'large'

export interface PhotoModalProps {
  smallUrl?: string | null
  largeUrl?: string | null
  initialSize?: PhotoSize
  onClose: () => void
  title?: string
}

export default function PhotoModal({ smallUrl, largeUrl, initialSize = 'small', onClose, title = 'Photo preview' }: PhotoModalProps) {
  const hasSmall = Boolean(smallUrl)
  const hasLarge = Boolean(largeUrl)
  const preferredSize: PhotoSize = (initialSize === 'large' && hasLarge) || (!hasSmall && hasLarge) ? 'large' : 'small'
  const [size, setSize] = React.useState<PhotoSize>(preferredSize)

  React.useEffect(() => {
    const nextPreferred: PhotoSize = (initialSize === 'large' && hasLarge) || (!hasSmall && hasLarge) ? 'large' : 'small'
    setSize(nextPreferred)
  }, [initialSize, hasLarge, hasSmall])

  const activeSize: PhotoSize = size === 'large' && hasLarge ? 'large' : hasSmall ? 'small' : 'large'
  const src = activeSize === 'small' ? smallUrl || largeUrl : largeUrl || smallUrl

  function stopPropagation(event: React.MouseEvent) {
    event.stopPropagation()
  }

  function handleKey(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClose()
    }
  }

  return (
    <div
      className="photo-modal-backdrop"
      onClick={onClose}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-label="Close photo preview"
    >
      <div className="photo-modal" onClick={stopPropagation} role="dialog" aria-modal="true">
        <div className="photo-modal-header">
          <span>{title}</span>
          <div className="photo-modal-controls">
            {hasSmall && hasLarge && (
              <div className="photo-size-toggle" role="group" aria-label="Photo size">
                <button
                  type="button"
                  className={activeSize === 'small' ? 'active' : ''}
                  onClick={() => setSize('small')}
                >
                  Small
                </button>
                <button
                  type="button"
                  className={activeSize === 'large' ? 'active' : ''}
                  onClick={() => setSize('large')}
                >
                  Large
                </button>
              </div>
            )}
            <button className="small" onClick={onClose} type="button">
              Close
            </button>
          </div>
        </div>
        {src ? (
          <img src={src} alt="Log entry attachment" />
        ) : (
          <div className="photo-modal-empty">Photo unavailable.</div>
        )}
        <div className="photo-modal-links">
          {hasSmall && (
            <a href={smallUrl || undefined} target="_blank" rel="noreferrer">
              Open small
            </a>
          )}
          {hasLarge && (
            <a href={largeUrl || undefined} target="_blank" rel="noreferrer">
              Open large
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
